import { z } from 'zod';

const slideBaseSchema = z.object({
  notes: z.string().optional(),
});

// ---- Visual DEVICE union (each stat/change slide picks ONE) ----
const labelPairSchema = z.object({
  a: z.string().min(1).max(28), // e.g. "Before" / "Us"
  b: z.string().min(1).max(28), // e.g. "After"  / "Them"
});

const waffleDeviceSchema = z.object({
  type: z.literal('waffle'),
  pct: z.number().min(0).max(100), // share filled with accent; rounded to whole cells at render
  label: z.string().min(1).max(48), // direct label riding the grid, e.g. "of owners have no AI"
  tone: z.enum(['positive', 'alarming']).default('positive'), // positive→accent, alarming→accent2 (amber-role)
});

const pairedBarDeviceSchema = z.object({
  type: z.literal('paired-bar'),
  a: z.number().min(0), // reference / "before" value (dim foil)
  b: z.number().min(0), // point / "after" value (accent)
  labels: labelPairSchema,
  unit: z.string().max(8).default(''), // "%", "x", "K", "" — rides the value label
});

const slopeDeviceSchema = z.object({
  type: z.literal('slope'),
  from: z.number(), // t0 value (dim dot, left)
  to: z.number(), // t1 value (accent dot, right)
  labels: labelPairSchema, // a=from label, b=to label (endpoint captions)
  unit: z.string().max(8).default(''),
});

const isotypeDeviceSchema = z.object({
  type: z.literal('isotype'),
  count: z.number().min(0).max(10), // filled icons (accent); fractional last icon allowed
  of: z.number().int().min(1).max(10), // total icons drawn (Neurath cap 10)
  glyph: z.enum(['person', 'block', 'dot']).default('person'),
  label: z.string().min(1).max(48), // "N in M owners…"
});

const arcDeviceSchema = z.object({
  type: z.literal('arc'),
  pct: z.number().min(0).max(100), // single proportion, filled arc on dim track
  label: z.string().min(1).max(48),
  tone: z.enum(['positive', 'alarming']).default('positive'),
});

const numberHeroDeviceSchema = z.object({
  type: z.literal('number-hero'),
  value: z.number(), // atomic figure; renderer formats + animates count-up
  prefix: z.string().max(4).default(''), // "$"
  suffix: z.string().max(4).default(''), // "%", "x", "M"
  decimals: z.number().int().min(0).max(2).default(0),
  texture: z.enum(['unit-grid', 'sparkline', 'bracket', 'rule']).default('unit-grid'),
  sparkline: z.array(z.number()).min(2).max(24).optional(), // required only when texture==='sparkline'
});

// SCISSOR: two diverging metrics on a shared baseline — the proof frame.
// e.g. leads UP +89% and cost-per-lead DOWN 25% from one "SAME BUDGET" anchor.
const scissorDeviceSchema = z.object({
  type: z.literal('scissor'),
  up: z.object({ value: z.number(), label: z.string().min(1).max(28), unit: z.string().max(8).default('') }), // the rising metric (accent, points up)
  down: z.object({ value: z.number(), label: z.string().min(1).max(28), unit: z.string().max(8).default('') }), // the falling metric (accent2/foil, points down)
  anchor: z.string().min(1).max(28).default('same budget'), // the shared baseline label
  footnote: z.string().max(40).optional(), // e.g. "501% net ROI" — small third figure, optional
});

export const StatDeviceSchema = z
  .discriminatedUnion('type', [
    waffleDeviceSchema,
    pairedBarDeviceSchema,
    slopeDeviceSchema,
    isotypeDeviceSchema,
    arcDeviceSchema,
    numberHeroDeviceSchema,
    scissorDeviceSchema,
  ])
  .superRefine((device, ctx) => {
    if (device.type === 'number-hero' && device.texture === 'sparkline' && (!device.sparkline || device.sparkline.length < 2)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sparkline texture requires a sparkline array with at least 2 points',
        path: ['sparkline'],
      });
    }
  });
export type StatDevice = z.infer<typeof StatDeviceSchema>;

const titleSlideSchema = slideBaseSchema.extend({
  kind: z.literal('title'),
  heading: z.string().min(1).max(260),
});

const bigStatSlideSchema = slideBaseSchema.extend({
  kind: z.literal('big-stat'),
  stat: z.string().min(1).max(80),
  statLabel: z.string().min(1).max(40).optional(),
  body: z.string().min(1).max(420),
  source: z.string().max(180).optional(),
  device: StatDeviceSchema.optional(), // hero device; falls back to bare `stat` text if omitted
  mechanism: z.object({
    type: z.literal('growth-flat'),
    growthLabel: z.string().min(1).max(40),
    flatLabel: z.string().min(1).max(40),
  }).optional(),
});

const bulletsSlideSchema = slideBaseSchema.extend({
  kind: z.literal('bullets'),
  heading: z.string().min(1).max(220),
  body: z.array(z.string().min(1).max(180)).min(1).max(5),
  device: z.discriminatedUnion('type', [waffleDeviceSchema, isotypeDeviceSchema, arcDeviceSchema]).optional(),
  kicker: z.string().min(2).max(16).optional(), // section eyebrow; renderer defaults to "Pattern"
});

const beforeAfterSlideSchema = slideBaseSchema.extend({
  kind: z.literal('before-after'),
  heading: z.string().min(1).max(220),
  body: z.object({
    before: z.string().min(1).max(320),
    after: z.string().min(1).max(320),
  }),
  device: z.discriminatedUnion('type', [pairedBarDeviceSchema, slopeDeviceSchema]).optional(),
});

const quoteSlideSchema = slideBaseSchema.extend({
  kind: z.literal('quote'),
  body: z.string().min(1).max(520),
  source: z.string().max(180).optional(),
  highlight: z.string().min(2).max(60).optional(), // key phrase rendered in accent when found in body
});

const ctaSlideSchema = slideBaseSchema.extend({
  kind: z.literal('cta'),
  heading: z.string().min(1).max(220),
  body: z.string().min(1).max(360),
  keyword: z.string().min(2).max(12).optional(), // the ONE word to remember; rendered as the hero pill
});

export const SlideDeckSlideSchema = z.discriminatedUnion('kind', [
  titleSlideSchema,
  bigStatSlideSchema,
  bulletsSlideSchema,
  beforeAfterSlideSchema,
  quoteSlideSchema,
  ctaSlideSchema,
]);

export const SlideDeckSpecSchema = z.object({
  brand: z.enum(['pvm', 'gdm', 'hai', 'ema', 'hd']),
  theme: z.string().min(1).max(260),
  hookTrigger: z.string().min(1).max(120),
  slides: z.array(SlideDeckSlideSchema).min(1).max(12),
});

export const SlideDeckPropsSchema = z.object({
  spec: SlideDeckSpecSchema,
  aspectRatio: z.enum(['16:9', '9:16']).default('16:9'),
  sceneSeconds: z.number().min(1).max(30).default(4),
  fps: z.number().int().min(1).max(60).default(30),
});

export type SlideDeckSpec = z.infer<typeof SlideDeckSpecSchema>;
export type SlideDeckSlide = z.infer<typeof SlideDeckSlideSchema>;
export type SlideDeckResolvedProps = z.infer<typeof SlideDeckPropsSchema>;
export type SlideDeckProps = z.input<typeof SlideDeckPropsSchema>;

export const getDefaultSlideDeckSpec = (): SlideDeckSpec => ({
  brand: 'hai',
  theme: 'Turn one approved idea into every weekly asset',
  hookTrigger: 'leverage',
  slides: [
    {
      kind: 'title',
      heading: 'One slot becomes the deck, video, email, and post.',
      notes: 'Open on the production leverage.',
    },
    {
      kind: 'big-stat',
      stat: '1x',
      body: 'Approve the idea once, then render every downstream asset from the same spec.',
      source: 'HG Content command center',
      device: { type: 'waffle', pct: 89, label: 'of weekly assets trace back to one approved spec', tone: 'positive' },
    },
    {
      kind: 'bullets',
      heading: 'The workflow stays simple',
      body: ['Approve script', 'Approve SlideSpec', 'Render deck MP4', 'Publish through Postiz'],
    },
    {
      kind: 'cta',
      heading: 'Ship the weekly asset set',
      body: 'Keep the source of truth small and make every output traceable.',
    },
  ],
});

export function getSlideDeckMetrics(input: SlideDeckProps) {
  const props = SlideDeckPropsSchema.parse(input);
  return {
    fps: props.fps,
    width: props.aspectRatio === '9:16' ? 1080 : 1920,
    height: props.aspectRatio === '9:16' ? 1920 : 1080,
    durationInFrames: Math.max(1, Math.round(props.spec.slides.length * props.sceneSeconds * props.fps)),
  };
}

// Soft cross-niche guard: stat values are otherwise niche-agnostic numbers, so this is
// a warning list (not a hard schema failure) that flags a device carrying a figure known
// to belong to the OTHER niche's proof points — e.g. a gdm (paving) deck citing the
// garage-only 133/59/501 figures, or a pvm (garage) deck citing the paving-only 89/25 figures.
const GARAGE_ONLY_VALUES = [133, 59, 501];
const PAVING_ONLY_VALUES = [89, 25];

function collectDeviceNumbers(device: StatDevice): number[] {
  switch (device.type) {
    case 'waffle':
    case 'arc':
      return [device.pct];
    case 'paired-bar':
      return [device.a, device.b];
    case 'slope':
      return [device.from, device.to];
    case 'isotype':
      return [device.count, device.of];
    case 'number-hero':
      return [device.value];
    case 'scissor':
      return [device.up.value, device.down.value];
    default:
      return [];
  }
}

export function validateNoCrossedStats(spec: SlideDeckSpec): string[] {
  if (spec.brand !== 'pvm' && spec.brand !== 'gdm') return [];

  // A pvm deck must never contain GARAGE-only figures (and vice versa) — flag the OTHER brand's values.
  const flagged = spec.brand === 'pvm' ? GARAGE_ONLY_VALUES : PAVING_ONLY_VALUES;
  const warnings: string[] = [];

  spec.slides.forEach((slide, index) => {
    const device = 'device' in slide ? slide.device : undefined;
    if (!device) return;

    for (const value of collectDeviceNumbers(device)) {
      if (flagged.includes(value)) {
        warnings.push(
          `Slide ${index + 1} (${slide.kind}) device value ${value} matches a known ${
            spec.brand === 'pvm' ? 'garage (gdm)' : 'paving (pvm)'
          }-only figure — verify this stat belongs to the ${spec.brand} niche.`,
        );
      }
    }
  });

  return warnings;
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
